import { summarizeAwsCron } from "utils/awsCron";
import type { PendingValue, PendingValueKind } from "./PendingChangesContext";

type Json = Record<string, unknown>;

/** Structural equality for pending-change values (configs are plain JSON). */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || a == null || b == null)
    return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    return (
      a.length === (b as unknown[]).length &&
      a.every((v, i) => deepEqual(v, (b as unknown[])[i]))
    );
  }
  const keysA = Object.keys(a as Json);
  const keysB = Object.keys(b as Json);
  return (
    keysA.length === keysB.length &&
    keysA.every(
      (k) =>
        Object.prototype.hasOwnProperty.call(b, k) &&
        deepEqual((a as Json)[k], (b as Json)[k]),
    )
  );
}

export function truncateJson(v: unknown, max = 60): string {
  const s = JSON.stringify(v) ?? "null";
  return s.length > max ? `${s.slice(0, max - 1)}...` : s;
}

export interface ConfigKeyDiff {
  key: string;
  kind: "changed" | "added" | "removed";
  oldText?: string;
  newText?: string;
}

/** Compact per-top-level-key diff for the review dialog. */
export function diffConfig(oldCfg: Json, newCfg: Json): ConfigKeyDiff[] {
  const keys = Array.from(
    new Set([...Object.keys(oldCfg ?? {}), ...Object.keys(newCfg ?? {})]),
  ).sort();
  const out: ConfigKeyDiff[] = [];
  for (const key of keys) {
    const inOld = oldCfg != null && key in oldCfg;
    const inNew = newCfg != null && key in newCfg;
    if (inOld && !inNew) {
      out.push({ key, kind: "removed", oldText: truncateJson(oldCfg[key]) });
    } else if (!inOld && inNew) {
      out.push({ key, kind: "added", newText: truncateJson(newCfg[key]) });
    } else if (!deepEqual(oldCfg[key], newCfg[key])) {
      out.push({
        key,
        kind: "changed",
        oldText: truncateJson(oldCfg[key]),
        newText: truncateJson(newCfg[key]),
      });
    }
  }
  return out;
}

export function formatPendingValue(
  kind: PendingValueKind,
  value: PendingValue,
): string {
  switch (kind) {
    case "enabled":
      return value ? "Enabled" : "Disabled";
    case "active":
      return value ? "Active" : "Inactive";
    case "cron":
      return summarizeAwsCron(String(value));
    case "config":
      return truncateJson(value);
    default:
      return `"${String(value)}"`;
  }
}
